import {
  IconBookmarkSmall,
  IconClose,
  IconLanguageSmall,
  IconPlay,
  IconStarSmall,
} from '../../screens/conversation/icons.js';
import { useState } from 'react';
import type { PhraseInfo } from './useWordSelection.js';
import type { MeaningLookup, WordLookupEntry } from './types.js';
import { normalizeWord } from './types.js';
import { MeaningBody, ipaLabel, shownMeaning } from './MeaningBody.js';
import { useSfx } from '../../lib/sfx/index.js';
import './word-lookup.css';

/**
 * Popover anclado bajo la palabra/frase tocada. Compartido por la conversación
 * y el karaoke del player (Fase 19.F). Tres modos:
 *   • palabra        → traducción + tipo (+ ipa/cefr/senses/ejemplo si `rich`)
 *   • frase-pendiente → botón "Traducir frase" (sólo si `enablePhrase`)
 *   • frase-resuelta  → loading → traducción
 *
 * `rich` (player) muestra los campos ricos del léxico de la Biblioteca
 * (ipa, cefr, acepciones, ejemplo). Sin `rich` (conversación) el render es
 * idéntico al histórico: sólo traducción + definición.
 *
 * Con el «significado de esa frase» (`wordEntry.meaning`) el cuerpo es
 * `MeaningBody` y el encabezado dice la clase, el nivel y la pronunciación del
 * significado que se muestra — que el alumno puede cambiar tocando otro chip.
 *
 * La ficha de la Biblioteca la usa igual al tocar una palabra adentro
 * (encargo 66), anclada sobre la palabra tocada.
 */
interface WordPopoverProps {
  isPhrase: boolean;
  word: string;
  phraseText: string;
  phraseWordCount: number;
  wordEntry: WordLookupEntry | null;
  wordLoading: boolean;
  phraseInfo: PhraseInfo | null;
  anchor: { cx: number; top: number };
  saved: boolean;
  playing: boolean;
  /**
   * Por qué no sonó el último "Escuchar", ya en copy para el usuario (o `null`
   * si no falló). Sin esto el botón se quedaba MUDO: `useAudioBlob` se traga el
   * error y el botón volvía a decir "Escuchar" como si no hubiera pasado nada
   * — que es exactamente lo que ve alguien frenado por el tope de audios de
   * palabra. Ver `describeSpeakError` en `lib/englishApi.ts`.
   */
  playError?: string | null;
  /** Habilita el modo frase (hint de extensión + UI de frase). Default true. */
  enablePhrase?: boolean;
  /** Muestra los campos ricos del léxico (ipa/cefr/senses/ejemplo). Default false. */
  rich?: boolean;
  /** Anclar ARRIBA de la palabra (cuando abajo no hay lugar). Default false. */
  flip?: boolean;
  /**
   * Cuánto se corre la flecha desde el centro (px) para seguir señalando la
   * palabra cuando el popover se corrió para no salirse del borde. Default 0.
   */
  arrowDx?: number;
  /**
   * Abre la ficha del diccionario desde el «significado de esa frase» (en el
   * significado elegido, o en la palabra si no se pudo elegir). Sin esto, el
   * botón no aparece.
   */
  onOpenMeaning?: (m: MeaningLookup) => void;
  /** La frase donde se tocó la palabra: si el ejemplo del diccionario es ésa, no se repite. */
  sentence?: string;
  /** En lugar de abrir la ficha, este aviso (la Biblioteca, con el tope de fichas lleno). */
  openNotice?: string | null;
  onConfirmPhrase: () => void;
  onPlay: () => void;
  /** Con el «significado de esa frase», recibe la traducción del significado que se muestra. */
  onSave: (translation?: string) => void;
  onClose: () => void;
}

export function WordPopover({
  isPhrase,
  word,
  phraseText,
  phraseWordCount,
  wordEntry,
  wordLoading,
  phraseInfo,
  anchor,
  saved,
  playing,
  playError = null,
  enablePhrase = true,
  rich = false,
  flip = false,
  arrowDx = 0,
  onOpenMeaning,
  sentence,
  openNotice = null,
  onConfirmPhrase,
  onPlay,
  onSave,
  onClose,
}: WordPopoverProps) {
  const { play } = useSfx();
  const loading = phraseInfo?.status === 'loading';
  const ready = phraseInfo?.status === 'ready';
  const phraseError = phraseInfo?.status === 'error';
  const result = phraseInfo?.result;

  const ipa = rich ? wordEntry?.ipa : null;
  const cefr = rich ? wordEntry?.cefr : null;
  const senses = rich && wordEntry?.senses ? wordEntry.senses : [];
  const example = rich ? wordEntry?.examples?.[0] : undefined;

  // El significado que se muestra: el elegido, o el chip que tocó el alumno.
  // La elección vale para ESTA respuesta: otra palabra tocada arranca de cero.
  const [chosen, setChosen] = useState<{ for: WordLookupEntry; id: string } | null>(null);
  const meaning = !isPhrase && !wordLoading ? wordEntry?.meaning : undefined;
  const shown = meaning
    ? shownMeaning(meaning, chosen && chosen.for === wordEntry ? chosen.id : null)
    : null;
  // Una palabra en español (`not-english`) no se escucha ni se guarda como si fuera inglés:
  // ni en la charla ni en la ficha.
  const actions = meaning?.kind !== 'not-english';
  const lexEntry = meaning?.kind === 'picked' || meaning?.kind === 'unchosen' ? meaning.entry : null;
  const meaningIpa = lexEntry ? (shown?.ipa ?? lexEntry.ipa) : null;
  // La pronunciación es la de la entrada (`run`), no la de la forma tocada (`runs`): se dice cuál.
  const ipaOf = lexEntry && normalizeWord(word) !== normalizeWord(lexEntry.text) ? lexEntry.text : null;
  const headPos = shown ? shown.pos : wordEntry?.partOfSpeech;
  const headCefr = shown ? shown.cefr : cefr;
  const openLabel = meaning?.kind === 'picked' || shown ? 'Ver ficha' : meaning?.kind === 'unchosen' ? 'Ver sus significados' : null;
  const openMeaning = (): void => {
    if (!onOpenMeaning || !meaning) return;
    onOpenMeaning(lexEntry && shown ? { kind: 'picked', entry: lexEntry, meaningId: shown.id } : meaning);
  };

  return (
    <div
      className={`word-pop ${flip ? 'is-flip' : 'slide-up-tight'}`}
      role="dialog"
      aria-label={isPhrase ? phraseText : word}
      style={{
        position: 'absolute',
        top: anchor.top,
        left: anchor.cx,
        transform: flip ? 'translate(-50%, -100%)' : 'translateX(-50%)',
        zIndex: 30,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        className={`word-pop-arrow ${flip ? 'down' : ''}`}
        style={arrowDx ? { left: `calc(50% + ${arrowDx}px)` } : undefined}
      />
      <div className="word-pop-head">
        <div className="word-pop-title">
          {isPhrase ? (
            <span className="word-pop-phrase">{phraseText}</span>
          ) : (
            <span className="word-pop-word">{word}</span>
          )}
          {isPhrase ? (
            <span className="word-pop-kind purple">{phraseWordCount} palabras</span>
          ) : (
            <>
              {headPos && <span className="word-pop-kind">{headPos}</span>}
              {headCefr && <span className="word-pop-kind cefr">{headCefr}</span>}
            </>
          )}
        </div>
        <button className="word-pop-x" onClick={onClose} aria-label="Cerrar">
          <IconClose size={10} />
        </button>
      </div>

      {/* ── Palabra individual ── */}
      {!isPhrase &&
        (wordLoading ? (
          <div className="word-pop-loading">
            <div className="dots-anim spinner" />
            <span>
              Buscando<span className="dots-anim" />
            </span>
          </div>
        ) : wordEntry && meaning ? (
          <>
            {meaningIpa && (
              <div className="word-pop-ipa is-meaning">
                {ipaOf && <span className="word-pop-ipa-of">{ipaOf}</span>}
                {ipaLabel(meaningIpa)}
              </div>
            )}
            <MeaningBody
              entry={wordEntry}
              meaning={meaning}
              word={word}
              sentence={sentence}
              shown={shown}
              onChoose={(id) => setChosen({ for: wordEntry, id })}
            />
          </>
        ) : wordEntry ? (
          <>
            {ipa && <div className="word-pop-ipa">{ipa}</div>}
            <div className="word-pop-es">
              <span className="word-pop-es-flag">ES</span>
              <span>{wordEntry.translation}</span>
            </div>
            {senses.length > 1 && (
              <div className="word-pop-senses">
                {senses.slice(0, 4).map((s, i) => (
                  <span key={i} className="word-pop-sense">
                    {s}
                  </span>
                ))}
              </div>
            )}
            {wordEntry.definition && <div className="word-pop-lit">{wordEntry.definition}</div>}
            {example && (
              <div className="word-pop-ex">
                <span className="word-pop-ex-q">&ldquo;</span>
                {example}
                <span className="word-pop-ex-q">&rdquo;</span>
              </div>
            )}
          </>
        ) : (
          <div className="word-pop-empty">No pudimos traducir esta palabra.</div>
        ))}

      {/* ── Frase: pendiente ── */}
      {isPhrase && !phraseInfo && (
        <div className="word-pop-pending">Frase seleccionada — confirmala para ver el significado.</div>
      )}
      {/* ── Frase: buscando ── */}
      {isPhrase && loading && (
        <div className="word-pop-loading">
          <div className="dots-anim spinner" />
          <span>
            Buscando frase<span className="dots-anim" />
          </span>
        </div>
      )}
      {/* ── Frase: resuelta ── */}
      {isPhrase && ready && result && (
        <>
          <div className="word-pop-es">
            <span className="word-pop-es-flag">ES</span>
            <span>{result.translation}</span>
          </div>
          <div className="word-pop-src">Traducción del contexto</div>
        </>
      )}
      {isPhrase && phraseError && (
        <div className="word-pop-empty">{phraseInfo?.error ?? 'No pudimos traducir la frase.'}</div>
      )}

      {/* ── Acciones ── */}
      {isPhrase && !phraseInfo ? (
        <div className="word-pop-actions">
          <button className="word-pop-act primary wide" onClick={onConfirmPhrase}>
            <IconLanguageSmall size={12} />
            <span>Traducir frase</span>
          </button>
        </div>
      ) : loading || wordLoading || !actions ? null : (
        <div className="word-pop-actions">
          <button className="word-pop-act primary" onClick={onPlay}>
            {playing ? (
              <div className="bars playing" style={{ height: 9, color: 'var(--lime)' }}>
                <span />
                <span />
                <span />
                <span />
              </div>
            ) : (
              <IconPlay size={9} />
            )}
            <span>{playing ? 'Reproduciendo…' : 'Escuchar'}</span>
          </button>
          <button
            className={`word-pop-act ghost ${saved ? 'is-saved' : ''}`}
            onClick={() => {
              if (!saved) play('word-saved'); // suena al guardar, no al quitar
              if (shown) onSave(shown.tr);
              else onSave();
            }}
            aria-label={saved ? 'Guardado en vocabulario' : 'Guardar al vocabulario'}
          >
            {saved ? <IconStarSmall size={11} /> : <IconBookmarkSmall size={11} />}
            <span>{saved ? 'Guardada' : 'Guardar'}</span>
          </button>
        </div>
      )}

      {playError && <div className="word-pop-empty">{playError}</div>}

      {onOpenMeaning &&
        openLabel &&
        (openNotice ? (
          <div className="word-pop-empty word-pop-meaning-open">{openNotice}</div>
        ) : (
          <button className="word-pop-act ghost word-pop-meaning-open" onClick={openMeaning}>
            {openLabel}
          </button>
        ))}

      {/* ── Hint de extensión (solo palabra, solo con frase habilitada) ── */}
      {!isPhrase && enablePhrase && (
        <div className="word-pop-extend">
          <span className="word-pop-extend-dot" />
          Tocá otra palabra para formar una frase
        </div>
      )}
    </div>
  );
}
